import ApiFactory from '@/factories/ApiFactory';
import UtilityService, { prepAutocompleteData, serialize } from '@/services/UtilityService';
import ExportService from '@/services/ExportService';
import store from '@/store';
import moment from 'moment';

const list = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`posweb/v1/orders?${query}`).then(
			({ data }) => {
				resolve(data.data);
			},
			(error) => reject(error)
		);
	});
};

const dashboard = (filters) => {
	return new Promise((resolve, reject) => {
		const query = serialize(filters);
		ApiFactory.get(`posweb/v1/orders/dashboard?${query}`).then(
			({ data }) => {
				data.data.orders = transformOrders(data.data.orders);
				resolve(data.data);
			},
			(error) => reject(error)
		);
	});
};

const prepareOrderData = (order) => {
	const calcItemPrice = (item) => {
		const itemPrice = item.variantItem?.id ? item.variantItem.price : item.itemPrice;
		const toppingsPrice = item.toppingItems?.length
			? item.toppingItems
					.map((topItem) => topItem.quantity * topItem.price)
					.reduce((a, b) => {
						return a + b;
					}, 0)
			: 0;
		return itemPrice + toppingsPrice;
	};

	return {
		discount: order.discount,
		manually: order.manually,
		transportCost: order.transportCost,
		serviceFee: order.serviceFee,
		items: order.items.map((i) => ({
			id: i.id,
			itemName: i.itemName,
			itemPrice: calcItemPrice(i),
			quantity: i.quantity,
			variantItem: i.variantItem && {
				id: i.variantItem.id,
				price: i.variantItem.price,
			},
			toppingItems:
				i.toppingItems &&
				i.toppingItems.map((top) => ({
					id: top.id,
					toppingTypeId: top.toppingTypeId,
					quantity: top.quantity,
					price: top.price,
				})),
		})),
		restaurant: {
			restaurantID: order.restaurant,
		},
		total: order.total,
		customer: {
			id: order.customer.id,
			phone: order.customer.phone,
			phonePrefix: order.customer.phonePrefix,
		},
		customerAddress: {
			id: order.customerAddress.id,
			latitude: order.customerAddress.latitude,
			longitude: order.customerAddress.longitude,
			addressStreet: order.customerAddress.addressStreet,
			addressDetails: order.customerAddress.addressDetails,
			primary: order.customerAddress.primary,
		},
		description: order.description,
		createdByUser: {
			id: store.getters.user.id,
		},
		takeAwayPoint: order.takeAwayPoint ? { id: order.takeAwayPoint.id } : {},
		isManualOrderCashless: order.isManualOrderCashless,
		notRegisteredUser: order.notRegisteredUser,
	};
};

const calcIsScheduleOrder = (order) => {
	if (order.scheduled) {
		const registered = moment(order.registrationtime);
		const scheduled = moment(order.scheduled);
		const diff = moment.duration(scheduled.diff(registered));
		return diff.asMinutes() > 5;
	} else {
		return false;
	}
};

const transformOrders = (orders) =>
	orders.map((order) => ({
		...order,
		isScheduledOrder: calcIsScheduleOrder(order),
		isTakeAwayOrder: order.takeAwayPoint && order.takeAwayPoint.id > 0,
	}));

const getWaitersList = (businessId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`posweb/v1/businesses/${businessId}/waiters`).then(
			({ data }) => {
				resolve(data.data);
			},
			(error) => reject(error)
		);
	});
};

const assignWaiterToOrder = (orderId, waiterId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.put(`posweb/v1/orders/${orderId}/waiters/${waiterId}`).then(
			({ data }) => {
				resolve(data.data);
			},
			(error) => reject(error)
		);
	});
};

const synchronizeManualOrder = (orderId) => {
	return new Promise((resolve, reject) => {
		ApiFactory.post(`posweb/v1/synchronize-manual-order`, { orderId }).then(
			({ data }) => {
				resolve(data.data);
			},
			(error) => reject(error)
		);
	});
};
const getPosSettings = () => {
	return new Promise((resolve, reject) => {
		ApiFactory.get(`systemadmin/v1/pos-settings`).then(
			({ data }) => {
				resolve(data.data);
			},
			(error) => reject(error)
		);
	});
};
export default {
	list,
	dashboard,
	getWaitersList,
	assignWaiterToOrder,
	synchronizeManualOrder,
	getPosSettings,
	getOrder(id) {
		return new Promise((resolve, reject) => {
			ApiFactory.get(`posweb/v1/orders/${id}`).then(
				({ data }) => {
					const order = data.data;
					order.isScheduledOrder = calcIsScheduleOrder(order);
					order.isTakeAwayOrder = order.takeAwayPoint && order.takeAwayPoint.id > 0;
					resolve(order);
				},
				(error) => reject(error)
			);
		});
	},
	getKanbanOrders(query) {
		return new Promise((resolve, reject) => {
			ApiFactory.get(`posweb/v1/orders/kanban-view?${serialize(query)}`).then(
				({ data }) => {
					let enableCookingTime = 0;
					const {
						accepted,
						accepted_to_pick_up,
						collected,
						completed,
						cooked,
						in_restaurant,
						pending,
					} = data.data;
					for (const [key, value] of Object.entries(data.data)) {
						if (
							(key === 'accepted_to_pick_up' || key === 'accepted') &&
							value.length &&
							value[0].enableCookingTime
						) {
							enableCookingTime = value[0].enableCookingTime;
						}
					}
					const orders = {
						pending: transformOrders(pending),
						accepted: enableCookingTime
							? transformOrders([].concat(accepted, accepted_to_pick_up))
							: transformOrders(accepted),
						in_progress: enableCookingTime
							? transformOrders([].concat(collected, cooked, in_restaurant))
							: transformOrders([].concat(accepted_to_pick_up, collected, cooked, in_restaurant)),
						completed: transformOrders(completed),
					};
					resolve(orders);
				},
				(error) => reject(error)
			);
		});
	},
	changeOrderStatus(orderId, payload) {
		return new Promise((resolve, reject) => {
			ApiFactory.put(`/justeat/v2/orders/${orderId}/status/${payload.status}`, payload.reason).then(
				(success) => resolve(success.data.data),
				(error) => reject(error)
			);
		});
	},
	updateOrderAddress(orderId, addressId) {
		return new Promise((resolve, reject) => {
			ApiFactory.put(`justeat/v2/orders/${orderId}/address/${addressId}`).then(
				(success) => resolve(success.data.data),
				(error) => reject(error)
			);
		});
	},
	getOrderPaymentType() {
		return new Promise((resolve, reject) => {
			ApiFactory.get(`justeat/v2/paymentTypes`).then(
				({ data }) => {
					resolve(data.data);
				},
				(error) => reject(error)
			);
		});
	},
	comments(id) {
		return new Promise((resolve, reject) => {
			ApiFactory.get(`posweb/v1/orders/${id}/comments`).then(
				({ data }) => {
					resolve(data.data);
				},
				(error) => reject(error)
			);
		});
	},
	addProducts(orderId, items) {
		return new Promise((resolve, reject) => {
			ApiFactory.post(`posweb/v1/${orderId}/add_items`, { items }).then(
				({ data }) => {
					resolve(data.data);
				},
				(error) => reject(error)
			);
		});
	},
	export(data) {
		return new Promise((resolve, reject) => {
			const orders = data.map((order) => ({
				OrderCode: this.formatOrderId(order.orderID),
				Restaurant: order.restaurant.restaurantName,
				Time: UtilityService.filters.formatDate(order['registrationtime']),
				Address: order.restaurant.baboonCourier ? order.address : '',
				'Payment Method': order.paymentMethod ? order.paymentMethod.paymentType : '',
				'Discount Type': order.discountValue ? order.discount.discountType : '',
				Subtotal: order.totalNet + order.discountValue,
				Discount: order.discountValue,
				'Transport Cost': order.restaurant.baboonCourier ? order['transportCost'] : '',
				Total: order.total,
				Status: UtilityService.filters.orderStatus(order.status),
			}));
			ExportService.export(orders, 'Orders', 'true').then(resolve, reject);
		});
	},
	create(data) {
		return new Promise((resolve, reject) => {
			try {
				const order = prepareOrderData(data);
				ApiFactory.post('posweb/v1/orders/', order).then(resolve, reject);
			} catch (e) {
				reject('Please make sure that all the fields are completed');
			}
		});
	},
	createComplex(data) {
		return new Promise((resolve, reject) => {
			try {
				const order = prepareOrderData(data);
				ApiFactory.post('justeat/v2/orders/', order).then(resolve, reject);
			} catch (e) {
				reject('Please make sure that all the fields are completed');
			}
		});
	},
	addComment(id, data) {
		return new Promise((resolve, reject) => {
			ApiFactory.post(`justeat/v2/orders/${id}/comments`, data).then(resolve, reject);
		});
	},
	getRejectReasonTypes() {
		return new Promise((resolve, reject) => {
			ApiFactory.get(`orders/v1/reject-reasons/types`).then(
				({ data }) => {
					let _res = Object.keys(data.data).map((key) => ({
						id: key,
						value: key,
						label: data.data[key],
					}));
					resolve(_res);
				},
				(error) => reject(error)
			);
		});
	},
	getRejectReason(type) {
		return new Promise((resolve, reject) => {
			ApiFactory.get(`orders/v1/reject-reasons?active=1&type=${type}`).then(
				({ data }) => {
					resolve(prepAutocompleteData(data.data, 'description', 'id'));
				},
				(error) => reject(error)
			);
		});
	},
	removeProduct(orderId, itemId) {
		return new Promise((resolve, reject) => {
			ApiFactory.put(`/orders/v1/${orderId}/remove_item/${itemId}`).then(resolve, reject);
		});
	},
	getAddressFromCoords(lat, lng) {
		return new Promise((resolve, reject) => {
			ApiFactory.get(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`).then(
				({ data }) => {
					resolve({
						street: data.address.road || data.address.suburb,
					});
				},
				(error) => reject(error)
			);
		});
	},
	updateAddressAndOrderAddress(orderId, customerId, address) {
		return new Promise((resolve, reject) => {
			ApiFactory.put(`justeat/v2/customers/${customerId}/addresses/${address.id}`, address).then(
				() => {
					ApiFactory.put(`justeat/v2/orders/${orderId}/address/${address.id}`).then(resolve, reject);
				},
				(error) => reject(error)
			);
		});
	},
	printFiscalInvoice(id) {
		return new Promise((resolve, reject) => {
			ApiFactory.post(`posweb/v1/reattempt-invoice-print?${serialize(id)}`).then(resolve, reject);
		});
	},
	formatOrderId(id) {
		if (!id) {
			return '';
		}
		id = id.toString();
		return id.toString().substr(id.length - 3, 3);
	},
	setPackageNo(id, packagesNo) {
		return new Promise((resolve, reject) => {
			ApiFactory.put(`posweb/v1/orders/${id}/packages-no/`, { packagesNo }).then(resolve, reject);
		});
	},
	acceptOrder(id, cookingTime) {
		return new Promise((resolve, reject) => {
			ApiFactory.put(`posweb/v1/orders/${id}/status/ACCEPTED`, { cookingTime }).then(resolve, reject);
		});
	},
	rejectOrder(id, statusValue, reason) {
		return new Promise((resolve, reject) => {
			ApiFactory.put(`posweb/v1/orders/${id}/status/${statusValue}`, reason).then(resolve, reject);
		});
	},
	cookOrder(id) {
		return new Promise((resolve, reject) => {
			ApiFactory.put(`posweb/v1/orders/${id}/status/COOKED`).then(resolve, reject);
		});
	},
	deliverOrder(id) {
		return new Promise((resolve, reject) => {
			ApiFactory.put(`posweb/v1/orders/${id}/status/DELIVERED`).then(resolve, reject);
		});
	},
	saveExtraDeliveryTime(payload, id) {
		return new Promise((resolve, reject) => {
			ApiFactory.post(`posweb/v1/businesses/${id}/extraTime`, payload).then(resolve, reject);
		});
	},
	deleteExtraDeliveryTime(id, status) {
		return new Promise((resolve, reject) => {
			ApiFactory.put(`posweb/v1/businesses/${id}/extraTime/status/${status}`).then(resolve, reject);
		});
	},
};
